const BASE_URL = process.env.REACT_APP_API_URL || "https://pg.singapay.test";
const API_PREFIX = "/api/v1/payment-linking/public";

const SESSION_TOKEN_KEY = "payment_linking_session_token";

class PaymentLinkingApiService {
  constructor() {
    this.sessionToken = null;
    try {
      this.sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
    } catch (error) {
      this.sessionToken = null;
    }
    this.requestQueue = [];
    this.isProcessingQueue = false;
  }

  rememberSessionToken(token) {
    this.sessionToken = token || null;
    try {
      if (this.sessionToken) {
        sessionStorage.setItem(SESSION_TOKEN_KEY, this.sessionToken);
      } else {
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
      }
    } catch (error) {
      // The tab can still finish this page without persistence.
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Extract session token dari response
   */
  extractSessionToken(response) {
    console.log("[API] Extracting session token from response...");

    const responseHeaders = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    console.log("[API] Response headers:", responseHeaders);

    return this.sessionToken;
  }

  /**
   * Enhanced fetch dengan retry logic
   */
  async fetchWithRetry(url, options, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const fetchOptions = {
          ...options,
        };

        console.log(`[API] Attempt ${attempt}/${maxRetries}:`, {
          url,
          method: fetchOptions.method,
          hasSessionToken: !!fetchOptions.headers?.['X-Session-Token'],
        });

        const response = await fetch(url, fetchOptions);

        // Handle 401 Unauthorized - session expired
        if (response.status === 401) {
          this.clearSession();
          throw new Error("Session expired. Please refresh the page.");
        }

        return response;
      } catch (error) {
        lastError = error;

        // Don't retry on certain errors
        const noRetryErrors = [
          "Session expired",
          "Access denied",
          "Payment link not found",
          "Payment already processed",
          "reached maximum usage",  // response_code 10
          "has expired",  // response_code 11
          "is disabled",  // response_code 12
          "not available",  // response_code 13
          "Invalid payment link",  // response_code 14
          "Invalid session",  // response_code 16
        ];

        const shouldNotRetry = noRetryErrors.some(msg =>
          error.message?.includes(msg)
        );

        if (shouldNotRetry || attempt === maxRetries) {
          // Enhance error message for network errors
          if (error.name === 'TypeError' && error.message.includes('fetch')) {
            error.message = "Network error. Please check your internet connection.";
          }
          throw error;
        }

        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
        console.log(`[API] Retry ${attempt + 1} after ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  }

  /**
   * Handle API response
   */
  async handleResponse(response) {
    // Extract session token from response
    this.extractSessionToken(response);

    // Parse response
    const contentType = response.headers.get("content-type");
    let data;

    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      const text = await response.text();
      throw new Error(
        `Unexpected response type: ${contentType}. Response: ${text}`,
      );
    }

    // Check for API error
    if (!response.ok || data.response_code !== "00") {
      const errorMessage = this.getErrorMessage(data, response.status);
      const error = new Error(errorMessage);
      error.status = response.status;
      error.code = data.response_code;
      error.data = data;
      throw error;
    }

    // Extract session_token from response data if available
    if (data.data && data.data.session_token) {
      this.rememberSessionToken(data.data.session_token);
      console.log(
        `[API] ✅ Session token stored: ${this.sessionToken.substring(0, 20)}...`
      );
    }

    return data;
  }

  /**
   * Get user-friendly error message from API error response
   */
  getErrorMessage(data, status) {
    // Backend-provided message (highest priority)
    if (data.message) {
      return data.message;
    }

    // Backend-provided response text
    if (data.response_text) {
      return data.response_text;
    }

    // Custom response codes from backend
    const responseCodeMessages = {
      "10": "This payment link has reached its maximum usage limit and is no longer valid.",
      "11": "Payment link has expired.",
      "12": "Payment link is disabled.",
      "13": "Payment link is not available.",
      "14": "Invalid payment link.",
      "15": "Session expired. Please refresh the page.",
      "16": "Invalid session token.",
      "17": "Payment method not supported.",
      "18": "Invalid payment amount.",
      "19": "Payment validation failed.",
      "20": "Payment processing failed.",
    };

    if (data.response_code && responseCodeMessages[data.response_code]) {
      return responseCodeMessages[data.response_code];
    }

    // HTTP status-based messages
    const statusMessages = {
      400: "Invalid request. Please check your input.",
      401: "Session expired. Please refresh the page.",
      403: "Access denied.",
      404: "Payment link not found.",
      409: "Payment already processed.",
      422: "Validation failed. Please check your payment details.",
      429: "Too many requests. Please wait a moment.",
      500: "Server error. Please try again.",
      502: "Payment gateway is temporarily unavailable.",
      503: "Service unavailable. Please try again later.",
      504: "Request timeout. Please try again.",
    };

    return statusMessages[status] || `An error occurred (Status: ${status})`;
  }

  /**
   * Prepare request headers with X-Session-Token
   */
  prepareHeaders(options = {}) {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...options.headers,
    };

    // Add X-Session-Token header if available
    if (this.sessionToken) {
      headers["X-Session-Token"] = this.sessionToken;
      console.log(`[API] 📤 Sending X-Session-Token header: ${this.sessionToken.substring(0, 20)}...`);
    } else {
      console.log(`[API] ⚠️ No session token available - request may fail!`);
    }

    return headers;
  }

  // ==================== API METHODS ====================

  /**
   * 1. Initialize Session
   */
  async initializeSession(linkCode, fcmToken = null, deviceInfo = null) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/session`;

    const body = {};
    if (fcmToken) body.fcm_token = fcmToken;
    if (deviceInfo) body.device_info = deviceInfo;

    console.log("[API] Initializing session for:", linkCode);

    const response = await this.fetchWithRetry(url, {
      method: "POST",
      headers: this.prepareHeaders(),
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse(response);
  }

  /**
   * 2. Get Payment Link Details
   */
  async getPaymentLinkDetails(linkCode) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}`;

    const response = await this.fetchWithRetry(url, {
      method: "GET",
      headers: this.prepareHeaders(),
    });

    return this.handleResponse(response);
  }

  /**
   * 3. Calculate Fee (Currently disabled)
   */
  async calculateFee(
    linkCode,
    paymentMethodId,
    paymentType,
    installmentMonths = 0,
  ) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/calculate-fee`;

    const response = await this.fetchWithRetry(url, {
      method: "POST",
      headers: this.prepareHeaders(),
      body: JSON.stringify({
        payment_method_id: paymentMethodId,
        payment_type: paymentType,
        payment_details: {
          installment_months: installmentMonths,
        },
      }),
    });

    return this.handleResponse(response);
  }

  /**
   * 4. Process Payment
   */
  async processPayment(linkCode, paymentData, idempotencyKey = null) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/process`;

    const headers = {};
    if (idempotencyKey) {
      headers["X-Idempotency-Key"] = idempotencyKey;
    }

    const response = await this.fetchWithRetry(url, {
      method: "POST",
      headers: this.prepareHeaders({ headers }),
      body: JSON.stringify(paymentData),
    });

    return this.handleResponse(response);
  }

  /**
   * 5. Check Payment Status
   */
  async checkPaymentStatus(linkCode, transactionId = null) {
    let url = `${BASE_URL}${API_PREFIX}/${linkCode}/status`;

    if (transactionId) {
      const params = new URLSearchParams({ transaction_id: transactionId });
      url += `?${params.toString()}`;
    }

    const response = await this.fetchWithRetry(url, {
      method: "GET",
      headers: this.prepareHeaders(),
    });

    return this.handleResponse(response);
  }

  /**
   * 6. Get Saved Methods
   */
  async getSavedMethods(linkCode) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/saved-methods`;

    const response = await this.fetchWithRetry(url, {
      method: "GET",
      headers: this.prepareHeaders(),
    });

    return this.handleResponse(response);
  }

  /**
   * 7. Update FCM Token
   */
  async updateFcmToken(linkCode, fcmToken) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/fcm-token`;

    const response = await this.fetchWithRetry(url, {
      method: "PUT",
      headers: this.prepareHeaders(),
      body: JSON.stringify({ fcm_token: fcmToken }),
    });

    return this.handleResponse(response);
  }

  /**
   * 8a. Initiate GoPay Account Linking (Midtrans tokenization)
   *
   * Triggers a Midtrans /v2/pay/account binding for the customer's
   * GoPay account. The backend creates a PENDING saved instrument
   * row and returns the Gojek-app activation URL we should
   * redirect the customer to. Final binding completion arrives
   * asynchronously via the Midtrans Pay Account notification
   * webhook on the backend; the SPA then sees the saved
   * instrument flip to ACTIVE on the next status poll / page
   * refresh.
   *
   * @param {string} linkCode
   * @param {object} [options]
   * @param {string} [options.phoneNumber] Override the link's customer_phone (optional)
   * @param {string} [options.redirectUrl] Override the default /b2b/linking/{code}/result redirect
   * @returns {Promise<{activation_url: string, account_id: string, saved_method_id: number}>}
   */
  async initiateGoPayLinking(linkCode, options = {}) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/gopay/initiate`;
    const body = {};
    if (options.phoneNumber) body.phone_number = options.phoneNumber;
    if (options.redirectUrl) body.redirect_url = options.redirectUrl;

    const response = await this.fetchWithRetry(url, {
      method: "POST",
      headers: this.prepareHeaders(),
      body: JSON.stringify(body),
    });

    return this.handleResponse(response);
  }

  /**
   * 8b. Initiate ShopeePay Account Linking (SNAP direct debit)
   *
   * Requests an `authCode` from ShopeePay and returns the
   * `authorization_url` we should full-tab redirect the customer
   * to. Unlike GoPay, ShopeePay's linking completes SYNCHRONOUSLY —
   * ShopeePay bounces the customer's browser back to our
   * `redirect_url` with `authCode` + `partnerReferenceNo` + `result`
   * as query params, and we must then call
   * {@link completeShopeepayLinking} ourselves (there is no
   * async webhook to wait on for the linking step itself).
   *
   * @param {string} linkCode
   * @param {object} [options]
   * @param {string} [options.phoneNumber]
   * @param {string} [options.redirectUrl] Override the default /b2b/linking/{code}/result redirect
   * @returns {Promise<{authorization_url: string, saved_method_id: number}>}
   */
  async initiateShopeepayLinking(linkCode, options = {}) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/shopeepay/initiate`;
    const body = {};
    if (options.phoneNumber) body.phone_number = options.phoneNumber;
    if (options.redirectUrl) body.redirect_url = options.redirectUrl;

    const response = await this.fetchWithRetry(url, {
      method: "POST",
      headers: this.prepareHeaders(),
      body: JSON.stringify(body),
    });

    return this.handleResponse(response);
  }

  /**
   * 8c. Complete ShopeePay Account Linking
   *
   * Called once the customer lands back on our `redirect_url` with
   * `authCode` (+ `partnerReferenceNo` + `result`) after authorizing
   * on ShopeePay. Exchanges the authCode for a persistent
   * accountToken on the backend and promotes the saved instrument
   * to ACTIVE.
   *
   * @param {string} linkCode
   * @param {object} params
   * @param {string} params.authCode
   * @param {string} [params.partnerReferenceNo]
   * @param {string} [params.result]
   * @returns {Promise<{saved_method_id: number}>}
   */
  async completeShopeepayLinking(linkCode, { authCode, partnerReferenceNo, result } = {}) {
    const query = new URLSearchParams();
    if (authCode) query.set("authCode", authCode);
    if (partnerReferenceNo) query.set("partnerReferenceNo", partnerReferenceNo);
    if (result) query.set("result", result);

    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/shopeepay/callback?${query.toString()}`;

    const response = await this.fetchWithRetry(url, {
      method: "GET",
      headers: this.prepareHeaders(),
    });

    return this.handleResponse(response);
  }

  /**
   * 9. Refresh Session
   */
  async refreshSession(linkCode) {
    const url = `${BASE_URL}${API_PREFIX}/${linkCode}/session/refresh`;

    const response = await this.fetchWithRetry(url, {
      method: "POST",
      headers: this.prepareHeaders(),
      body: JSON.stringify({}),
    });

    return this.handleResponse(response);
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Check session validity
   */
  async validateSession(linkCode) {
    try {
      await this.getPaymentLinkDetails(linkCode);
      return true;
    } catch (error) {
      if (error.status === 401) {
        this.clearSession();
      }
      return false;
    }
  }

  /**
   * Clear all session data
   */
  clearSession() {
    this.rememberSessionToken(null);
    console.log("[API] Session cleared");
  }

  /**
   * Get current session state
   */
  getSessionState() {
    return {
      hasSession: !!this.sessionToken,
      sessionToken: this.sessionToken,
    };
  }

  /**
   * Set session token (useful if you want to manually set the token)
   */
  setSessionToken(token) {
    this.rememberSessionToken(token);
    console.log(`[API] Session token set: ${token.substring(0, 20)}...`);
  }

  /**
   * Auto-refresh session jika hampir expired
   */
  async autoRefreshSessionIfNeeded(linkCode, expiresAt) {
    if (!expiresAt) return false;

    const expires = new Date(expiresAt);
    const now = new Date();
    const timeUntilExpiry = expires.getTime() - now.getTime();

    // Refresh jika sisa waktu kurang dari 5 menit
    if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
      try {
        await this.refreshSession(linkCode);
        console.log("[API] Session auto-refreshed");
        return true;
      } catch (error) {
        console.error("[API] Auto-refresh failed:", error);
        return false;
      }
    }

    return false;
  }
}

// Export singleton instance
export const paymentLinkingApi = new PaymentLinkingApiService();
